import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { getSupabaseClient } from '@/config/supabase';

/**
 * Step 2 of the password reset flow. The emailed link redirects here with a
 * recovery token that Supabase installs as a short-lived session on load. We
 * confirm that session exists before showing the form, then set the new password
 * via updateUser and send the (now fully authenticated) user to the dashboard.
 */
export function ResetPasswordPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'checking' until we know whether a recovery session landed; 'invalid' means
  // the link was missing/expired/already used.
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid'>('checking');

  useEffect(() => {
    const supabase = getSupabaseClient();
    let resolved = false;

    const markReady = () => {
      if (resolved) return;
      resolved = true;
      setStatus('ready');
    };

    // Supabase parses the recovery token from the URL on client init and emits a
    // PASSWORD_RECOVERY (or SIGNED_IN) event; this catches it if it lands late.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) markReady();
    });

    // Usually the session is already installed by the time we mount.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        markReady();
      } else {
        // Give the URL-detection listener a brief window before giving up.
        setTimeout(() => {
          if (!resolved) setStatus('invalid');
        }, 1500);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { error: updateError } = await auth.updatePassword(password);
      if (updateError) {
        setError(updateError);
        return;
      }
      navigate('/', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-purple to-brand-purple-light">
      <div className="w-full max-w-md rounded-xl bg-white p-10 shadow-2xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-brand-purple">PathChron</h1>
          <p className="text-sm text-gray-500">Enterprise Device Management</p>
        </div>

        {status === 'checking' && (
          <p className="text-center text-sm text-gray-500">Verifying your reset link...</p>
        )}

        {status === 'invalid' && (
          <div className="text-center">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-brand-black">Link expired</h2>
              <p className="mt-2 text-sm text-gray-500">
                This password reset link is invalid or has expired. Request a new one to continue.
              </p>
            </div>
            <Link to="/forgot-password" className="font-medium text-brand-purple hover:underline">
              Request a new link
            </Link>
          </div>
        )}

        {status === 'ready' && (
          <>
            <div className="mb-6 text-center">
              <h2 className="text-lg font-semibold text-brand-black">Choose a new password</h2>
              <p className="mt-1 text-sm text-gray-400">Enter and confirm your new password</p>
            </div>

            {error && (
              <div className="mb-4 rounded-lg bg-brand-orange-light p-3 text-sm text-brand-orange">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-sm font-medium text-brand-black"
                >
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                  placeholder="At least 6 characters"
                />
              </div>
              <div>
                <label
                  htmlFor="confirm"
                  className="mb-1 block text-sm font-medium text-brand-black"
                >
                  Confirm password
                </label>
                <input
                  id="confirm"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple"
                  placeholder="Re-enter your password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-3 rounded-lg bg-brand-purple px-6 py-3 font-medium text-white shadow-sm transition-colors hover:bg-brand-purple/90 disabled:opacity-50"
              >
                {submitting ? 'Updating...' : 'Update password'}
              </button>
            </form>
          </>
        )}

        <p className="mt-8 text-center text-xs text-gray-400">
          PathChron — Enterprise Device Management
        </p>
      </div>
    </div>
  );
}
